import yaml from 'js-yaml'
import { YamlConfig } from './YamlConfig'
interface Settings {
  domain: string
  audience: string
  organizationId: string
  clientId: string
  accountsApiUrl: string
  workspaceApiUrl: string
  workflowsApiUrl: string
  notifierUrl: string
  apiBaseUrl: string
  accountsUrl: string
  mapsApiUrl: string
  mapsPublicUrl: string
  recurlyUrl: string
  publicUrl: string
  hubspotId: string
  statsApiUrl: string
  hubspotLimitFormId: string
  hubspotSubscriptionFormId: string
  allScopes: string
  doCatalogApiUrl: string
  googleMapsApiKey: string
  importApiUrl: string
  publicCatalogUrl: string
  bigqueryOAuthEnabled: boolean
  enableFeaturedApplications: boolean
  enableTrackJs?: boolean
  localStorageOrgIdKey: string
  studentClientId: string
  loginAsStudentKey: string
  launchDarklyClientSideId: string
  cartoVersion: string
  documentationUrl: string
  pricingUrl: string
  feedbackUrl: string
  supportEmail: string
  exportBucket: string
  exportUrl: string
  enableExports: boolean
  enableGenAi: boolean
  askNicelyMinHoursOldEnough: number
}

const settings: Settings = {
  domain: '',
  audience: '',
  organizationId: '',
  clientId: '',
  accountsApiUrl: '',
  workspaceApiUrl: '',
  workflowsApiUrl: '',
  notifierUrl: '',
  apiBaseUrl: '',
  accountsUrl: '',
  statsApiUrl: '',
  publicUrl: import.meta.env.VITE_PUBLIC_URL || '',
  publicCatalogUrl: '',
  mapsApiUrl: '',
  mapsPublicUrl: '',
  recurlyUrl: '',
  hubspotId: '',
  hubspotLimitFormId: '',
  hubspotSubscriptionFormId: '',
  doCatalogApiUrl: '',
  googleMapsApiKey: '',
  allScopes:
    'read:current_user update:current_user read:connections write:connections read:maps write:maps read:account admin:account',
  importApiUrl: '',
  bigqueryOAuthEnabled: false,
  enableFeaturedApplications: false,
  localStorageOrgIdKey: 'orgId',
  studentClientId: '',
  loginAsStudentKey: 'student_account',
  launchDarklyClientSideId: '',
  cartoVersion: '',
  documentationUrl: 'https://docs.carto.com',
  pricingUrl: 'https://carto.com/pricing/',
  feedbackUrl: 'https://cartohq.typeform.com/to/a4YHHwPS',
  supportEmail: 'support@carto.com',
  exportBucket: 'gs://carto-tnt-gcp-us-east1-export-storage',
  exportUrl: 'https://storage.googleapis.com/carto-tnt-gcp-us-east1-export-storage',
  enableExports: true,
  enableGenAi: true,
  askNicelyMinHoursOldEnough: 0
}

export const DEFAULT_ANALYTICS_TOOLBOX_PROJECT = 'carto-un'

export async function loadConfig() {
  const configUrl = import.meta.env.VITE_APP_CONFIG_URL || '/config.yaml'

  const config = await fetchConfigYaml(configUrl)

  applyConfig(config)
}

export async function applyConfig(config: YamlConfig) {
  const { askNicely, auth0, apis, hubspot, launchDarkly, cartoVersion } = config

  // AUTH0
  settings.domain = auth0.domain
  settings.audience = auth0.audience
  settings.clientId = auth0.clientId
  settings.studentClientId = auth0.studentClientId
  settings.organizationId = auth0.organizationId

  // APIs
  settings.apiBaseUrl = apis.baseUrl
  settings.accountsApiUrl = apis.accountsUrl
  settings.workspaceApiUrl = apis.workspaceUrl
  settings.workflowsApiUrl = apis.workflowsUrl ? `${apis.workflowsUrl}/workflows` : `${apis.baseUrl}/v3/workflows`
  settings.notifierUrl = apis.notifierUrl
  settings.mapsApiUrl = `${apis.baseUrl}/v3/maps`
  settings.statsApiUrl = `${apis.baseUrl}/v3/stats`
  settings.doCatalogApiUrl = apis.doUrl
  settings.importApiUrl = `${apis.importUrl || apis.baseUrl}/v3/imports`
  settings.recurlyUrl = `${apis.recurlyUrl}`

  // Hubspot
  settings.hubspotId = hubspot.id
  settings.hubspotLimitFormId = hubspot.limitFormId
  settings.hubspotSubscriptionFormId = hubspot.requestSubscriptionFormId

  // LaunchDarkly
  settings.launchDarklyClientSideId = launchDarkly.clientSideId

  // CARTO Version
  const selfHosted = cartoVersion.selfHosted
  settings.cartoVersion = selfHosted ? `${selfHosted}` : 'SaaS'

  // TrackJS
  settings.enableTrackJs = import.meta.env.PROD && config.enableTrackJs

  // Export features
  const enableExportsNotConfigured =
    config.enableExports === '' || config.enableExports === undefined || config.enableExports === null
  settings.enableExports = enableExportsNotConfigured ? true : Boolean(config.enableExports)

  // GenAi features
  const enableGenAiNotConfigured =
    config.enableGenAi === '' || config.enableGenAi === undefined || config.enableGenAi === null
  settings.enableGenAi = enableGenAiNotConfigured ? true : Boolean(config.enableGenAi)

  // common
  const accountsUrl = config.accountsUrl.endsWith('/')
    ? config.accountsUrl.substring(0, config.accountsUrl.length - 1)
    : config.accountsUrl // ensure we don't have a trailing slash
  settings.accountsUrl = accountsUrl
  settings.mapsPublicUrl = config.publicMapUrl
  settings.googleMapsApiKey = config.googleMapsApiKey
  settings.publicCatalogUrl = config.publicCatalogUrl

  // TODO: this flag should be managed in Backend (maybe in /me),
  // they already have a flag that we could use to decide if we show or hide OAuth option
  settings.bigqueryOAuthEnabled = config.bigqueryOAuthEnabled
  settings.enableFeaturedApplications = config.enableFeaturedApplications

  // AskNicely NPS
  // Originally the check was against 'account' date.
  // We won't rename the variable (infra side / env variable), now that user creation date is considered
  settings.askNicelyMinHoursOldEnough = askNicely.minHoursAccountOldEnough
}

export function parseYamlConfig(data: string): YamlConfig {
  try {
    return yaml.load(data) as YamlConfig
  } catch (e) {
    const msg = e instanceof Error ? e.message : ''
    throw new Error(`Error parsing config file. ${msg}`)
  }
}

export async function fetchConfigYaml(configUrl: string) {
  let yamlText
  try {
    const response = await fetch(configUrl)

    if (!response.ok) {
      throw new Error(`File status code ${response.status}.`)
    }
    yamlText = await response.text()
    if (!yamlText) {
      throw new Error(`Empty config file detected.`)
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : ''
    console.log(e)
    throw new Error(`Cannot download file ${configUrl}. ${msg}`)
  }

  return parseYamlConfig(yamlText)
}

export default settings
